import { untrack } from "svelte";
import { setSheetDisplayNone, sheetScope, unsetSheetDisplayNone } from "../utils/props";
import { stack } from "../stack/global.svelte";
import { dispatchSheetEvent } from "../event/event.dispatch";
import {
  SHEET_EVENT_AFTER_CLOSE,
  SHEET_EVENT_AFTER_OPEN,
  SHEET_EVENT_CLOSE,
  SHEET_EVENT_FLYIN,
  SHEET_EVENT_FLYOUT,
  SHEET_EVENT_OPEN,
} from "../event/event.consts";
import type { OcSheetV1InstanceState } from "./stack.svelte";

const log = /*           */ sheetScope.scope("visibility");

const SHEET_OPEN_CLOSE_ANIMATION_DURATION = 210;

export const teleportSheetToBody = (host: HTMLOcSheetV1Element, placeholder: Comment): void => {
  /*                                                                    */
  if (import.meta.env.STORYBOOK) return;

  /*                                                     */
  if (host.parentNode === document.body) return;

  log.info("Teleporting sheet to body", host.id);
  host.parentNode?.replaceChild(placeholder, host);
  document.body.appendChild(host);
};

export const teleportSheetBack = (host: HTMLOcSheetV1Element, placeholder: Comment): void => {
  /*                                                                    */
  if (import.meta.env.STORYBOOK) return;

  /*                                                        */
  if (!placeholder.parentNode) return;

  log.info("Teleporting sheet back to original position", host.id);
  placeholder.parentNode.replaceChild(host, placeholder);
};

export const shouldBeginShow = (instanceState: OcSheetV1InstanceState): boolean =>
  instanceState.isCurrent && instanceState.isActive;

export const shouldBeginFlyIn = (instanceState: OcSheetV1InstanceState): boolean =>
  instanceState.isCurrent && instanceState.isActive && instanceState.isReady;

export const shouldCompleteShow = (instanceState: OcSheetV1InstanceState): boolean =>
  instanceState.isCurrent &&
  instanceState.isActive &&
  instanceState.isVisible &&
  !instanceState.isShown;

export const shouldBeginClose = (instanceState: OcSheetV1InstanceState): boolean =>
  !instanceState.isCurrent &&
  instanceState.isActive &&
  instanceState.isVisible &&
  !instanceState.isShown;

export const shouldCompleteClose = (instanceState: OcSheetV1InstanceState): boolean =>
  !instanceState.isCurrent && !instanceState.isActive;

export const shouldDispatchFlyIn = (instanceState: OcSheetV1InstanceState): boolean =>
  instanceState.isCurrent && instanceState.isActive && instanceState.isVisible;

export const shouldDispatchFlyOut = (instanceState: OcSheetV1InstanceState): boolean =>
  !instanceState.isCurrent && instanceState.isActive && !instanceState.isVisible;

export const withContentChange = (
  callback: () => void,
  timeout = SHEET_OPEN_CLOSE_ANIMATION_DURATION,
): number | undefined => {
  if (untrack(() => stack.contentChange)) return void callback();
  return window.setTimeout(callback, timeout);
};

export const forceReflow = (host: HTMLOcSheetV1Element): never => {
  return window.getComputedStyle(host).opacity as never; /*                                          */
};

/**
 *
 *
 *
 *
 *
 *
 *
 */
export const useSheetVisibility = (
  host: HTMLOcSheetV1Element,
  instanceState: OcSheetV1InstanceState,
): void => {
  /**
 *
 *
 */
  const placeholder = document.createComment(host.id);

  /**
 *
 *
 */
  let animationTimeout: number | undefined;

  /*                                                     */
  $effect(() => {
    const [action, , reason] = untrack(() => [...stack.action, stack.reason] as const);

    if (shouldBeginShow(instanceState)) {
      clearTimeout(animationTimeout);

      /*                            */
      teleportSheetToBody(host, placeholder);
      unsetSheetDisplayNone(host);

      /*                                */
      dispatchSheetEvent(SHEET_EVENT_OPEN, host, { reason, action });
      forceReflow(host);
    }
  });

  /*                     */
  $effect(() => {
    if (shouldBeginFlyIn(instanceState)) {
      withContentChange(() => {
        instanceState.isVisible = true;
      }, 0);
    }
  });

  /*                    */
  $effect(() => {
    if (shouldCompleteShow(instanceState)) {
      const [action, , reason] = untrack(() => [...stack.action, stack.reason] as const);
      animationTimeout = withContentChange(() => {
        instanceState.isShown = true;

        dispatchSheetEvent(SHEET_EVENT_AFTER_OPEN, host, { reason, action });
      });
    }
  });

  /*                                                 */
  $effect(() => {
    const [action, closeType, reason] = untrack(() => [...stack.action, stack.reason] as const);

    if (shouldDispatchFlyIn(instanceState)) {
      dispatchSheetEvent(SHEET_EVENT_FLYIN, host, { reason, action });
    }

    if (shouldDispatchFlyOut(instanceState)) {
      dispatchSheetEvent(SHEET_EVENT_FLYOUT, host, { reason, action, closeType });
    }
  });

  /*                  */
  $effect(() => {
    if (shouldBeginClose(instanceState)) {
      const [action, closeType, reason] = untrack(() => [...stack.action, stack.reason] as const);
      clearTimeout(animationTimeout);

      instanceState.isShown = false;
      instanceState.isVisible = false;

      animationTimeout = withContentChange(() => {
        /*                                   */
        dispatchSheetEvent(SHEET_EVENT_CLOSE, host, { reason, action, closeType });
        instanceState.isActive = false;
      });
    }
  });

  /*                                                    */
  let teleportEnabled = false;
  $effect(() => {
    if (shouldCompleteClose(instanceState)) {
      const [action, closeType, reason] = untrack(() => [...stack.action, stack.reason] as const);

      /*                                                  */
      /*                                                                   */
      setSheetDisplayNone(host);

      /*                                                           */
      if (teleportEnabled) {
        teleportSheetBack(host, placeholder);
        dispatchSheetEvent(SHEET_EVENT_AFTER_CLOSE, host, { reason, action, closeType });
      }
    }

    /*                                              */
    teleportEnabled = true;
  });
};
